import {
    CDK_TABLE,
    CdkCell,
    CdkCellDef,
    CdkColumnDef,
    CdkFooterCell,
    CdkFooterCellDef,
    CdkHeaderCell,
    CdkHeaderCellDef
} from '@angular/cdk/table';
import { Directive, ElementRef, HostBinding, Input, TemplateRef, forwardRef, OnInit, inject } from '@angular/core';

import { ApTableComponent } from '../table.component';

const cdkColReg = /^cdk-column-/;

/**
 * Get the index of the element
 * @param {HTMLElement}  elm
 * @returns {number}
 */
function getElementIndex(elm: Element): number {
    return Array.from(elm?.parentElement?.children || []).indexOf(elm);
}

/**
 * If a cell is in `<thead>` the column index is obvious.
 * If a cell is in `<tbody>` it may be preceded by an x-number of hidden cells.
 * So we check the cell className `cdk-column-[name]` to check the index of the corresponding cell in `<thead>`.
 * @param {HTMLElement} cell
 * @returns {number}
 */
function getCellColumnIndex(cell: Element): number {
    const colClassName = Array.from(cell.classList)
        .filter((name) => cdkColReg.test(name))
        .pop();
    const th = cell.closest('table').querySelector(`.${colClassName}`);
    return getElementIndex(th);
}

/** Cell definition for the ap-table. Captures the template of a column's data row cell as well as cell-specific properties. */
@Directive({
    selector: '[apCellDef]',
    providers: [{ provide: CdkCellDef, useExisting: ApCellDefDirective }]
})
export class ApCellDefDirective extends CdkCellDef {
    constructor() {
        const template = inject(TemplateRef<any>);
        super(template);
    }
}

/** Header cell definition for the ap-table. Captures the template of a column's header cell and as well as cell-specific properties. */
@Directive({
    selector: '[apHeaderCellDef]',
    providers: [{ provide: CdkHeaderCellDef, useExisting: ApHeaderCellDefDirective }]
})
export class ApHeaderCellDefDirective extends CdkHeaderCellDef {
    constructor() {
        const template = inject(TemplateRef<any>);
        super(template);
    }
}

/** Footer cell definition for the ap-table. Captures the template of a column's footer cell and as well as cell-specific properties. */
@Directive({
    selector: '[apFooterCellDef]',
    providers: [{ provide: CdkFooterCellDef, useExisting: ApFooterCellDefDirective }]
})
export class ApFooterCellDefDirective extends CdkFooterCellDef {
    constructor() {
        const template = inject(TemplateRef<any>);
        super(template);
    }
}

/** Column definition for the ap-table. Defines a set of cells available for a table column. */
@Directive({
    selector: '[apColumnDef]',
    providers: [{ provide: CdkColumnDef, useExisting: ApColumnDefDirective }]
})
export class ApColumnDefDirective extends CdkColumnDef {
    /** Unique name for this column. */
    @Input() set apColumnDef(value: string) {
        this.name = value;
    }

    constructor() {
        const _table = inject(
            forwardRef(() => CDK_TABLE),
            { optional: true }
        );
        super(_table);
    }
}

/** Header cell template container that adds the right classes. */
@Directive({ selector: 'ap-header-cell, th[ap-header-cell]' })
export class ApHeaderCellDirective extends CdkHeaderCell implements OnInit {
    private tableComponent = inject(ApTableComponent<any>);
    @HostBinding('class.ap-header-cell') apHeaderCell = true;
    @HostBinding('attr.id') protected id: string;

    private columnDef: CdkColumnDef;
    private elementRef: ElementRef<HTMLElement>;
    private isHidden = false;

    constructor() {
        const columnDef = inject(CdkColumnDef);
        const elementRef = inject(ElementRef<HTMLElement>);
        super(columnDef, elementRef);

        this.columnDef = columnDef;
        this.elementRef = elementRef;
    }

    ngOnInit() {
        this.setColId();
        this.elementRef.nativeElement.classList.add(`ap-column-${this.columnDef.cssClassFriendlyName}`);
    }

    private setColId() {
        const { nativeElement } = this.elementRef;
        const tr = nativeElement.closest('tr');
        const isInHead = tr?.parentElement.nodeName === 'THEAD';

        const colIndex =
            !isInHead && nativeElement.nextElementSibling
                ? getCellColumnIndex(nativeElement.nextElementSibling)
                : getElementIndex(nativeElement);

        const rowIndex = getElementIndex(tr);
        this.id = this.tableComponent.getAndStoreCellID(colIndex, rowIndex, this.isHidden);
    }

    hide() {
        this.isHidden = true;
        this.setColId();
    }
}

/** Footer cell template container that adds the right classes. */
@Directive({ selector: 'ap-footer-cell, td[ap-footer-cell]' })
export class ApFooterCellDirective extends CdkFooterCell {
    @HostBinding('class.ap-footer-cell') apFooterCell = true;

    constructor() {
        const columnDef = inject(CdkColumnDef);
        const elementRef = inject(ElementRef<HTMLElement>);
        super(columnDef, elementRef);

        elementRef.nativeElement.classList.add(`ap-column-${columnDef.cssClassFriendlyName}`);
    }
}

/** Cell template container that adds the right classes. */
@Directive({
    selector: 'ap-cell, td[ap-cell]'
})
export class ApCellDirective extends CdkCell implements OnInit {
    @HostBinding('class.ap-cell') apCell = true;
    private columnDef: CdkColumnDef;
    private elementRef: ElementRef<HTMLElement>;

    constructor() {
        const columnDef = inject(CdkColumnDef);
        const elementRef = inject(ElementRef<HTMLElement>);
        super(columnDef, elementRef);

        this.columnDef = columnDef;
        this.elementRef = elementRef;
    }

    ngOnInit() {
        const { nativeElement } = this.elementRef;

        nativeElement.classList.add(`ap-column-${this.columnDef.cssClassFriendlyName}`);
    }
}

/** Cell template container that adds the right classes. */
@Directive({ selector: 'ap-hidden-cell, td[ap-hidden-cell], th[ap-hidden-cell]' })
export class ApHiddenCellDirective extends ApCellDirective {
    @HostBinding('class.ap-hidden-cell') apHiddenCell = true;

    constructor() {
        super();
        const headerCellDirective = inject(ApHeaderCellDirective, { optional: true });
        headerCellDirective?.hide();
    }
}
